import { useEffect, useState } from 'react'
import { Button, Card, Form, Input, Modal, Select, Switch, Table, Tag, message } from 'antd'
import { EditOutlined, UserAddOutlined } from '@ant-design/icons'
import * as api from '../lib/api'
import { useI18n } from '../i18n/I18nProvider'
import Title from 'antd/es/typography/Title'

const ROLE_OPTIONS = [
  { value: 'hr_superadmin', label: 'HR Superadmin' },
  { value: 'hr_viewer', label: 'HR Viewer' }
]

export default function SessionHRUserManager({ sessionId, refreshKey = 0 }) {
  const { t } = useI18n()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editing, setEditing] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [form] = Form.useForm()

  async function loadUsers() {
    setLoading(true)
    try {
      setUsers(await api.getSessionHRUsers(sessionId))
    } catch (error) {
      message.error(error.message || t('session.hr_users.messages.load_failed', 'Unable to load HR users'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadUsers()
  }, [refreshKey, sessionId]) // eslint-disable-line react-hooks/exhaustive-deps

  function openCreate() {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({ role: 'hr_viewer', is_active: true })
    setModalOpen(true)
  }

  function openEdit(record) {
    setEditing(record)
    form.setFieldsValue(record)
    setModalOpen(true)
  }

  async function saveUser() {
    const values = await form.validateFields()
    setSaving(true)
    try {
      if (editing) {
        await api.updateSessionHRUser(sessionId, editing.id, values)
      } else {
        await api.createSessionHRUser(sessionId, values)
      }
      message.success(t('session.hr_users.messages.saved', 'HR user saved'))
      setModalOpen(false)
      await loadUsers()
    } catch (error) {
      message.error(error.message || t('session.hr_users.messages.save_failed', 'Unable to save HR user'))
    } finally {
      setSaving(false)
    }
  }

  const columns = [
    {
      title: t('common.name', 'Name'),
      key: 'name',
      render: (_, record) => [record.first_name, record.last_name].filter(Boolean).join(' ') || '—'
    },
    { title: t('common.email', 'Email'), dataIndex: 'email' },
    { title: t('common.position', 'Position'), dataIndex: 'position', render: value => value || '—' },
    {
      title: t('session.hr_users.role', 'Role'),
      dataIndex: 'role',
      render: value => ROLE_OPTIONS.find(option => option.value === value)?.label || value
    },
    {
      title: t('common.status', 'Status'),
      dataIndex: 'is_active',
      render: active => <Tag color={active ? 'green' : 'default'}>{active ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}</Tag>
    },
    {
      title: t('common.actions', 'Actions'),
      key: 'actions',
      width: 72,
      render: (_, record) => <Button icon={<EditOutlined />} aria-label={t('common.edit', 'Edit')} onClick={() => openEdit(record)} />
    }
  ]

  return (
    <>
      <Card
        size='small'
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>{t('session.hr_users.title', 'HR Users')}</Title>
          <Button type='primary' icon={<UserAddOutlined />} onClick={openCreate}>{t('session.hr_users.add', 'Add HR User')}</Button>
        </div>
        <Table
          rowKey='id'
          size='small'
          loading={loading}
          dataSource={users}
          columns={columns}
          pagination={false}
          locale={{ emptyText: t('session.hr_users.empty', 'No HR users are assigned to this session scope') }}
        />
      </Card>

      <Modal
        title={editing ? t('session.hr_users.edit', 'Edit HR User') : t('session.hr_users.add', 'Add HR User')}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={saveUser}
        confirmLoading={saving}
        destroyOnHidden
      >
        <Form form={form} layout='vertical'>
          <Form.Item name='email' label={t('common.email', 'Email')} rules={[{ required: true, type: 'email' }]}>
            <Input disabled={Boolean(editing)} />
          </Form.Item>
          <Form.Item name='first_name' label={t('common.first_name', 'First Name')}>
            <Input />
          </Form.Item>
          <Form.Item name='last_name' label={t('common.last_name', 'Last Name')}>
            <Input />
          </Form.Item>
          <Form.Item name='position' label={t('common.position', 'Position')}>
            <Input />
          </Form.Item>
          <Form.Item name='role' label={t('session.hr_users.role', 'Role')} rules={[{ required: true }]}>
            <Select options={ROLE_OPTIONS} />
          </Form.Item>
          <Form.Item name='is_active' label={t('common.active', 'Active')} valuePropName='checked'>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}